import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { LIVE, SLOW } from "./queries";

/**
 * SEO 수집 운영 (시안 e4ed46c seo-operations / seo-collection) 의 실제 API.
 * 키는 모두 ["seo", ...] 로 시작한다 → 실시간 신호(customer·audit)와 쓰기 뒤 무효화가 함께 적용된다.
 */

export type SeoPlatform = "naver_search" | "naver_searchad" | "google";
export type SeoConnectionStatus = "unchecked" | "ok" | "invalid" | "quota" | "disabled";
export const platformNames: Record<SeoPlatform, string> = { naver_search: "네이버 검색", naver_searchad: "네이버 검색광고", google: "구글" };
export const platformOrder: SeoPlatform[] = ["naver_search", "naver_searchad", "google"];
export const connectionStatus: Record<SeoConnectionStatus, [string, string]> = {
  ok: ["정상", "teal"],
  unchecked: ["확인 전", "amber"],
  invalid: ["키 거부", "coral"],
  quota: ["한도 초과", "amber"],
  disabled: ["중지", "slate"],
};

/** GET /admin/seo/connections. 사용량 필드는 모니터링 › SEO 수집에서만 쓴다 (고객사 화면은 키 정보만). */
export type SeoConnection = {
  id: string;
  customerId: string | null;
  customerName: string;
  platform: SeoPlatform;
  platformName: string;
  label: string;
  credentialHint: string | null;
  status: SeoConnectionStatus;
  shareable: boolean;
  dailyLimit: number | null;
  monthlyLimit: number | null;
  usedToday: number;
  usedMonth: number;
  lentMonth: number;
  usedMonthPct: number | null;
  quotaUntil: string | null;
  lastCheckedAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  createdAt: string;
};

export type SeoAssetKind = "naver_blog" | "naver_cafe" | "site" | "place" | "shop_store";
export const assetKindNames: Record<SeoAssetKind, string> = { naver_blog: "블로그", naver_cafe: "카페", site: "도메인", place: "업체명", shop_store: "판매처" };
export type SeoAsset = { id: string; kind: SeoAssetKind; matchKey: string; label: string | null; enabled: boolean; createdAt: string };

/** GET /admin/seo/customers/:id/results · GET /c/:id/seo/results (고객에게는 borrowed 없음). */
export type SeoResult = {
  keywordId: string;
  keyword: string;
  area: string;
  collectedAt: string;
  total: number | null;
  depth: number;
  found: boolean;
  bestRank: number | null;
  prevBestRank: number | null;
  ourCount: number;
  borrowed?: boolean;
};

export type SeoHistory = {
  keyword: string;
  series: { area: string; collectedAt: string; bestRank: number | null; found: boolean; ourCount: number; total: number | null }[];
  latest: { area: string; collectedAt: string; items: { rank: number; title: string; link: string; author: string; date: string; our: boolean }[] }[];
};

export type SeoUsageRow = {
  customerId: string;
  customerName: string;
  customerStatus: string;
  callsMonth: number;
  ownKeyCalls: number;
  borrowedCalls: number;
  lentCalls: number;
  callsToday: number;
  errorsMonth: number;
  trackingKeywords: number;
  failingKeywords: number;
  lastCollectedAt: string | null;
  connections: Partial<Record<SeoPlatform, SeoConnectionStatus>>;
  ownMonthlyLimit: number | null;
  ownKeyUsedMonth: number;
  ownKeyUsedPct: number | null;
};

export type SeoDailyUsage = { day: string; platform: SeoPlatform; calls: number; errors: number; borrowed: number };

export type SeoCollectSummary = {
  tracking: number;
  failing: number;
  collectedToday: number;
  connections: number;
  connectionsOk: number;
  connectionsProblem: number;
  nearLimit: { id: string; customerName: string; platform: SeoPlatform; usedMonthPct: number | null }[];
  warnPct: number;
};

export const SEO_POLICY_KEYS = ["seo_collect_interval_hours", "seo_collect_batch", "seo_collect_depth", "seo_quota_warn_pct", "seo_result_retention_days"] as const;

const qs = (o: Record<string, string | number | undefined>) => {
  const p = Object.entries(o).filter(([, v]) => v !== undefined && v !== "");
  return p.length ? `?${p.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join("&")}` : "";
};

export const useSeoConnections = (customerId: string | null | "shared", enabled = true) =>
  useQuery({
    queryKey: ["seo", "admin", "connections", customerId ?? "all"],
    queryFn: () => api.page<SeoConnection>(`/admin/seo/connections${qs({ customerId: customerId && customerId !== "shared" ? customerId : undefined })}`),
    select: (p) => (customerId === "shared" ? p.items.filter((c) => c.customerId === null) : p.items),
    refetchInterval: SLOW,
    enabled,
  });

export type SeoPostedBlog = { blogId: string; executorName: string; deviceName: string | null; posts: number; lastPostedAt: string };
export const useSeoPostedBlogs = (customerId: string) =>
  useQuery({ queryKey: ["seo", "admin", "assets", customerId, "posted"], queryFn: () => api.page<SeoPostedBlog>(`/admin/seo/customers/${customerId}/assets/posted`), enabled: !!customerId });
export const useSeoAssets = (customerId: string) =>
  useQuery({ queryKey: ["seo", "admin", "assets", customerId], queryFn: () => api.page<SeoAsset>(`/admin/seo/customers/${customerId}/assets`), enabled: !!customerId });

export const useAdminSeoResults = (customerId: string) =>
  useQuery({ queryKey: ["seo", "admin", "results", customerId], queryFn: () => api.page<SeoResult>(`/admin/seo/customers/${customerId}/results`), refetchInterval: SLOW, enabled: !!customerId });

export const useCustomerSeoResults = (customerId: string) =>
  useQuery({ queryKey: ["seo", customerId, "results"], queryFn: () => api.page<SeoResult>(`/c/${customerId}/seo/results`), refetchInterval: SLOW, enabled: !!customerId });

export const useSeoHistory = (customerId: string, keywordId: string | null, days = 30) =>
  useQuery({
    queryKey: ["seo", customerId, "history", keywordId, days],
    queryFn: () => api.get<SeoHistory>(`/c/${customerId}/seo/keywords/${keywordId}/history?days=${days}`),
    enabled: !!customerId && !!keywordId,
  });

export const useSeoUsage = (month: string) =>
  useQuery({ queryKey: ["seo", "admin", "usage", month], queryFn: () => api.page<SeoUsageRow>(`/admin/seo/usage${qs({ month })}`), refetchInterval: LIVE });

export const useSeoDailyUsage = (days: number, customerId?: string) =>
  useQuery({
    queryKey: ["seo", "admin", "usage-daily", days, customerId ?? "all"],
    queryFn: () => api.page<SeoDailyUsage>(`/admin/seo/usage/daily${qs({ days, customerId })}`),
    refetchInterval: SLOW,
  });

export type SeoCollectFailure = { customerId: string; customerName: string; keywordId: string; keyword: string; lastCollectError: string; lastCollectedAt: string | null };
export const useSeoCollectFailures = () =>
  useQuery({ queryKey: ["seo", "admin", "failures"], queryFn: () => api.page<SeoCollectFailure>("/admin/seo/collect/failures"), refetchInterval: SLOW });

export const useSeoCollectSummary = () =>
  useQuery({ queryKey: ["seo", "admin", "summary"], queryFn: () => api.get<SeoCollectSummary>("/admin/seo/collect/summary"), refetchInterval: LIVE });

/** 순위 변동: 이전보다 올랐으면 양수 (순위 숫자가 작아짐). */
export function rankDelta(r: Pick<SeoResult, "bestRank" | "prevBestRank">): number | null {
  if (r.bestRank === null || r.prevBestRank === null) return null;
  return r.prevBestRank - r.bestRank;
}
export function rankText(r: Pick<SeoResult, "bestRank" | "found" | "depth">): string {
  return r.found && r.bestRank !== null ? `${r.bestRank}위` : `상위 ${r.depth}위 밖`;
}
export function deltaText(d: number | null): string {
  if (d === null) return "변동 없음 · 첫 수집";
  if (d === 0) return "변동 없음";
  return d > 0 ? `▲ ${d}` : `▼ ${-d}`;
}
